import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

import MarkdownPreview from '~/components/Common/MarkdownPreview';

import { RateByGroup, RateGroupRecommendation, UngroupedRateDetails } from '~/services/cruises/RateGroupService';

interface Props {
  rate: RateByGroup;
  rateDetails?: UngroupedRateDetails;
  isDetailsLoading: boolean;
  isDetailsError: boolean;
  recommendation?: RateGroupRecommendation;
  isRecommendationWaiting: boolean;
  isRecommendationLoading: boolean;
  isRecommendationError: boolean;
  hasRateGroups?: boolean;
  onAcceptSuggestion: () => void;
  onChooseGroup: () => void;
}

function RateCodeCard({
  rate,
  rateDetails,
  isDetailsLoading,
  isDetailsError,
  recommendation,
  isRecommendationWaiting,
  isRecommendationLoading,
  isRecommendationError,
  hasRateGroups = false,
  onAcceptSuggestion,
  onChooseGroup,
}: Props) {
  const canRecommend =
    !!rateDetails &&
    !rateDetails.detailsFetchFailed &&
    rateDetails.description !== null &&
    rateDetails.isNonRefundable !== null;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography fontWeight={700}>{rate.code}</Typography>
        {!rate.departureId && <Chip label="No related departures" color="warning" size="small" sx={{ mt: 1 }} />}
        {isDetailsLoading ? (
          <CircularProgress size={20} aria-label={`Loading current rate details for ${rate.code}`} sx={{ mt: 2 }} />
        ) : (
          <>
            <Typography variant="subtitle2" mt={1}>
              Description
            </Typography>
            <Typography variant="body2">{rateDetails?.description ?? 'Unavailable'}</Typography>
            <Typography variant="subtitle2" mt={1}>
              Rate code remarks
            </Typography>
            <Typography variant="body2">{rateDetails?.rateCodeRemarks ?? 'Unavailable'}</Typography>
            {rateDetails?.isNonRefundable !== null && rateDetails?.isNonRefundable !== undefined && (
              <>
                <Typography variant="subtitle2" mt={1}>
                  Cancellation policy
                </Typography>
                <Typography variant="body2">{rateDetails.isNonRefundable ? 'Non-refundable' : 'Refundable'}</Typography>
              </>
            )}
            {(rateDetails?.rateCodeDetails || rateDetails?.rateCodeDetailsFetchFailed) && (
              <Accordion disableGutters elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Rate code details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {rateDetails.rateCodeDetailsFetchFailed ? (
                    <Alert severity="warning">Unable to fetch rate code details.</Alert>
                  ) : (
                    <MarkdownPreview content={rateDetails.rateCodeDetails} />
                  )}
                </AccordionDetails>
              </Accordion>
            )}
            {(isDetailsError || !rate.departureId || rateDetails?.detailsFetchFailed) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Unable to fetch current rate details.
              </Alert>
            )}

            <Typography variant="subtitle2" mt={2}>
              AI rate group recommendation
            </Typography>
            {(isRecommendationWaiting || (canRecommend && isRecommendationLoading)) && (
              <CircularProgress size={20} aria-label={`Loading recommendation for ${rate.code}`} sx={{ mt: 1 }} />
            )}
            {!isRecommendationWaiting && !canRecommend && (
              <Typography variant="body2">Current rate details are insufficient for a recommendation.</Typography>
            )}
            {!isRecommendationWaiting && canRecommend && isRecommendationError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Unable to create a rate group recommendation.
              </Alert>
            )}
            {recommendation && (
              <>
                <Typography variant="body2" mt={1}>
                  {recommendation.rateGroup ? (
                    <>
                      <strong>Suggested group:</strong> {recommendation.rateGroup.name}
                    </>
                  ) : (
                    'No suitable rate group suggested.'
                  )}
                </Typography>
                <Typography variant="body2">
                  <strong>Confidence:</strong> {recommendation.confidence}
                </Typography>
                <Typography variant="body2">
                  <strong>Explanation:</strong> {recommendation.rationale}
                </Typography>
              </>
            )}
            {(recommendation?.rateGroup || hasRateGroups) && (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                {recommendation?.rateGroup && (
                  <Button variant="contained" size="small" onClick={onAcceptSuggestion}>
                    Accept suggestion
                  </Button>
                )}
                {hasRateGroups && (
                  <Button variant="outlined" size="small" onClick={onChooseGroup}>
                    Assign specific group
                  </Button>
                )}
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default RateCodeCard;
